import { z } from 'zod';
import { mercuryIntlErrors, mercuryIntlInput } from './mercury-intl.js';

/** Representative payouts: verified shipments → four approvals → Airwallex / Mercury transfer → reconciliation. */

export const PAYOUT_PROVIDERS = ['airwallex', 'mercury', 'manual'] as const;
export type PayoutProvider = (typeof PAYOUT_PROVIDERS)[number];
export const PAYOUT_PROVIDER_LABELS: Record<PayoutProvider, string> = { airwallex: 'Airwallex', mercury: 'Mercury', manual: 'Manual payment' };

export const PAYOUT_METHODS = ['ach', 'same_day_ach', 'wire', 'rtp', 'swift', 'local'] as const;
export type PayoutMethod = (typeof PAYOUT_METHODS)[number];
export const PAYOUT_METHOD_LABELS: Record<PayoutMethod, string> = {
  ach: 'ACH',
  same_day_ach: 'Same-day ACH',
  wire: 'Domestic wire',
  rtp: 'Real-time payment',
  swift: 'SWIFT (international)',
  local: 'Local transfer (international)',
};

/**
 * disabled: replaced by a newer verified version (or a retired Mercury copy). removed: deliberately removed (or, while
 * still awaiting verification, withdrawn) by staff; the full details are wiped, the masked row stays for past payouts.
 */
export const BENEFICIARY_STATUSES = ['pending_verification', 'verified', 'rejected', 'disabled', 'removed'] as const;
export type BeneficiaryStatus = (typeof BENEFICIARY_STATUSES)[number];

/** Removing (or withdrawing) bank details: the reason is required and kept in the audit log and on the record. */
export const beneficiaryRemoveInput = z.object({ reason: z.string().trim().min(3, 'Give a reason').max(500) });
export type BeneficiaryRemoveInput = z.infer<typeof beneficiaryRemoveInput>;

export const PAYOUT_STATUSES = [
  'requested',
  'correction_requested',
  'returned',
  'jason_approved',
  'finance_approved',
  'ready_to_release',
  'owner_approved',
  'submission_pending',
  'submitted',
  'awaiting_bank_approval',
  'processing',
  'sent',
  'paid',
  'failed',
  'reversed',
  'cancelled',
  'rejected',
] as const;
export type PayoutStatus = (typeof PAYOUT_STATUSES)[number];

export const PAYOUT_STATUS_LABELS: Record<PayoutStatus, string> = {
  requested: 'Awaiting Jason review',
  correction_requested: 'Correction requested',
  returned: 'Returned to requester',
  jason_approved: 'Awaiting Finance approval',
  finance_approved: 'Awaiting final approval',
  ready_to_release: 'Approved — waiting for Finance to release',
  owner_approved: 'Approved — initiating',
  submission_pending: 'Submission pending',
  submitted: 'Submitted to provider',
  awaiting_bank_approval: 'Awaiting bank approval',
  processing: 'Processing',
  sent: 'Sent',
  paid: 'Paid / settled',
  failed: 'Failed',
  reversed: 'Reversed',
  cancelled: 'Cancelled',
  rejected: 'Rejected',
};

/** Statuses in which a request still holds its settlement lines (a line can be in one such request only). */
export const PAYOUT_ACTIVE: PayoutStatus[] = ['requested', 'correction_requested', 'returned', 'jason_approved', 'finance_approved', 'ready_to_release', 'owner_approved', 'submission_pending', 'submitted', 'awaiting_bank_approval', 'processing', 'sent', 'paid'];
/** Before the owner's approval the instruction can still change (and approvals are then superseded). */
export const PAYOUT_EDITABLE: PayoutStatus[] = ['requested', 'correction_requested', 'returned', 'jason_approved', 'finance_approved', 'ready_to_release'];
/**
 * A payment can be cancelled up to and including "Approved — waiting for Finance to release" (approved, not yet
 * released): never once Finance released it / it was submitted to the provider (then the failed / reversal flows apply).
 */
export const PAYOUT_CANCELLABLE: PayoutStatus[] = ['requested', 'correction_requested', 'returned', 'jason_approved', 'finance_approved', 'ready_to_release'];
/** Before any approval: the request can be cancelled without a reason by anyone who may raise requests. */
export const PAYOUT_PRE_APPROVAL: PayoutStatus[] = ['requested', 'correction_requested', 'returned'];
/** At the provider, waiting for a final status. */
export const PAYOUT_IN_FLIGHT: PayoutStatus[] = ['submitted', 'awaiting_bank_approval', 'processing', 'sent'];
export const PAYOUT_TERMINAL: PayoutStatus[] = ['paid', 'failed', 'reversed', 'cancelled', 'rejected'];

export const PAYOUT_STAGES = ['request', 'review', 'finance', 'final', 'manual_payment', 'release'] as const;
export type PayoutStage = (typeof PAYOUT_STAGES)[number];
export const PAYOUT_STAGE_LABELS: Record<PayoutStage, string> = {
  request: 'Requested',
  review: 'Jason review',
  finance: 'Finance approval',
  final: 'Owner final approval',
  manual_payment: 'Manual payment recorded',
  release: 'Released by Finance',
};

export const COURIER_ACCEPTANCE = ['accepted', 'pending', 'not_tracked', 'override'] as const;
export type CourierAcceptance = (typeof COURIER_ACCEPTANCE)[number];

/**
 * ownerApprovalThresholdCents: an approval request (a payroll run, or one payment alone) whose total is below this
 * (USD) is released on Jason's approval alone; at or above it the owner's final approval (TOTP) is needed as well.
 */
export const DEFAULT_PAYOUT_SETTINGS = { perPaymentMaxCents: 500_000, perDayMaxCents: 2_000_000, slaHours: 12, ownerApprovalThresholdCents: 50_000 };
export type PayoutSettings = typeof DEFAULT_PAYOUT_SETTINGS;

const id = z.string().uuid();
const optText = (max: number) => z.string().trim().max(max).nullish();

/** Airwallex beneficiary field values, keyed by API path (e.g. "beneficiary.bank_details.account_number"). */
/** ABA routing number check digit: 3·(d1+d4+d7) + 7·(d2+d5+d8) + (d3+d6+d9) ≡ 0 (mod 10). */
export function abaChecksumValid(aba: string): boolean {
  if (!/^\d{9}$/.test(aba)) return false;
  const d = aba.split('').map(Number);
  return (3 * (d[0] + d[3] + d[6]) + 7 * (d[1] + d[4] + d[7]) + (d[2] + d[5] + d[8])) % 10 === 0;
}
export const ABA_CHECKSUM_MESSAGE = "This routing number isn't valid (check digit doesn't match) — check it";

export const beneficiaryFieldValues = z.record(z.string().trim().max(120), z.string().trim().max(300));

export const beneficiaryInput = z
  .object({
    provider: z.enum(['airwallex', 'mercury', 'manual']),
    method: z.enum(PAYOUT_METHODS),
    holderName: z.string().trim().min(2).max(140),
    holderType: z.enum(['personal', 'company']).default('personal'),
    email: z.string().trim().email().max(200).nullish(),
    currency: z.string().trim().regex(/^[A-Za-z]{3}$/).transform((v) => v.toUpperCase()).default('USD'),
    bankCountry: z.string().trim().regex(/^[A-Za-z]{2}$/).transform((v) => v.toUpperCase()).default('US'),
    bankName: optText(140),
    accountNumber: z.string().trim().regex(/^[A-Za-z0-9 -]{4,34}$/, 'Enter the account number'),
    routingNumber: z.string().trim().regex(/^[A-Za-z0-9]{4,11}$/).nullish(),
    swiftCode: z.string().trim().regex(/^[A-Za-z0-9]{8}([A-Za-z0-9]{3})?$/).nullish(),
    accountType: z.enum(['checking', 'savings']).default('checking'),
    taxId: z.string().trim().max(20).nullish(),
    address: z.object({
      line1: z.string().trim().max(140).default(''),
      city: z.string().trim().min(1).max(80),
      state: optText(40),
      postcode: z.string().trim().max(20).default(''),
      country: z.string().trim().regex(/^[A-Za-z]{2}$/).transform((v) => v.toUpperCase()),
    }),
    /** Every field Airwallex asked for (from its beneficiary schema); sent to Airwallex as-is by path. */
    airwallexFields: beneficiaryFieldValues.optional(),
    /** Local clearing system chosen for the schema (e.g. ACH, FEDWIRE). */
    clearingSystem: z.string().trim().max(30).nullish(),
    /** Mercury, non-US bank: the international-wire details (bank city, purpose, country codes, intermediary bank). */
    intl: mercuryIntlInput.nullish(),
  })
  .superRefine((v, ctx) => {
    // With Airwallex's own schema, its required fields and patterns are checked against that schema instead.
    const schemaDriven = v.provider === 'airwallex' && !!v.airwallexFields && Object.keys(v.airwallexFields).length > 0;
    // Mercury international wires: not every country has postal codes (Mercury's own invite asks for what applies).
    const mercuryIntl = v.provider === 'mercury' && v.bankCountry !== 'US';
    if (!schemaDriven) {
      if (v.address.line1.length < 2) ctx.addIssue({ code: 'custom', path: ['address', 'line1'], message: 'Street address is required' });
      if (!v.address.postcode && !mercuryIntl) ctx.addIssue({ code: 'custom', path: ['address', 'postcode'], message: 'Postal code is required' });
    }
    if (['ach', 'same_day_ach', 'wire', 'rtp'].includes(v.method) && !/^\d{9}$/.test(v.routingNumber ?? '')) ctx.addIssue({ code: 'custom', path: ['routingNumber'], message: 'A 9-digit ABA routing number is required for US payments' });
    else if (['ach', 'same_day_ach', 'wire', 'rtp'].includes(v.method) && !abaChecksumValid(v.routingNumber!)) ctx.addIssue({ code: 'custom', path: ['routingNumber'], message: ABA_CHECKSUM_MESSAGE });
    if (v.method === 'swift' && !v.swiftCode) ctx.addIssue({ code: 'custom', path: ['swiftCode'], message: 'SWIFT code is required' });
    if (!schemaDriven && v.currency === 'NPR' && v.method === 'local') {
      if (v.bankCountry !== 'NP') ctx.addIssue({ code: 'custom', path: ['bankCountry'], message: 'Local NPR transfers go to banks in Nepal (country NP)' });
      if (!/^[A-Za-z0-9]{5}$/.test(v.routingNumber ?? '')) ctx.addIssue({ code: 'custom', path: ['routingNumber'], message: 'Enter the 5-character Nepal bank code (e.g. NP002)' });
    }
    if (v.currency === 'NPR' && v.method === 'swift' && !/^\d{9}$/.test(v.taxId ?? '')) ctx.addIssue({ code: 'custom', path: ['taxId'], message: 'NPR by SWIFT needs the person\'s 9-digit PAN' });
    if (v.method === 'local' && v.bankCountry === 'US') ctx.addIssue({ code: 'custom', path: ['method'], message: 'For US accounts choose ACH or wire' });
    if (v.provider === 'mercury' && v.currency !== 'USD') ctx.addIssue({ code: 'custom', path: ['currency'], message: 'Mercury pays in USD only (international wires are sent in USD; the receiving bank converts)' });
    if (v.method === 'rtp' && v.provider !== 'mercury') ctx.addIssue({ code: 'custom', path: ['method'], message: 'Real-time payments are sent through Mercury only' });
    if (v.provider === 'mercury' && !mercuryIntl) {
      // US accounts: ACH / RTP / domestic wire to a US recipient.
      if (v.address.country !== 'US') ctx.addIssue({ code: 'custom', path: ['address', 'country'], message: 'Mercury US recipients need a US address (country US)' });
      if (['swift', 'local'].includes(v.method)) ctx.addIssue({ code: 'custom', path: ['method'], message: 'For US accounts choose ACH, real-time payment or domestic wire' });
    }
    if (mercuryIntl) {
      // Any other country: an international (SWIFT) wire, sent in USD.
      if (v.method !== 'swift') ctx.addIssue({ code: 'custom', path: ['method'], message: 'Mercury pays bank accounts outside the US by international wire (SWIFT)' });
      for (const [k, message] of Object.entries(mercuryIntlErrors(v))) ctx.addIssue({ code: 'custom', path: k.split('.'), message });
    }
  });
export type BeneficiaryInput = z.infer<typeof beneficiaryInput>;

export const payoutCreateInput = z.object({
  representativeId: id,
  lineIds: z.array(id).min(1).max(500),
  beneficiaryId: id,
  fundingAccountId: z.string().trim().min(1).max(120),
  purpose: z.string().trim().min(3).max(300),
  note: optText(2000),
  clientRequestId: z.string().uuid().optional(),
  /** From "Pay representative": Jason approves (owner too at the threshold), then Finance releases it (Airwallex, or Mercury when usable). */
  release: z.boolean().optional(),
  /** Mercury bank details only: ACH or real-time payment for this payment (defaults to the bank details' method). */
  method: z.enum(['ach', 'rtp', 'wire', 'swift']).optional(),
  /**
   * "Mercury" chosen with the representative's verified US/USD Airwallex details (beneficiaryId): the same details are
   * reused as their Mercury recipient.
   */
  provider: z.enum(['airwallex', 'mercury']).optional(),
  /** Lines for clients already marked "rep paid": why they are paid again (Jason / the owner only). */
  overrideReason: optText(500),
});
export const payoutUpdateInput = z.object({
  lineIds: z.array(id).min(1).max(500).optional(),
  beneficiaryId: id.optional(),
  fundingAccountId: z.string().trim().min(1).max(120).optional(),
  purpose: z.string().trim().min(3).max(300).optional(),
  note: optText(2000),
  overrideReason: optText(500),
});
/** Cancel a payment before it is released (a reason is required once it has any approval). */
export const payoutCancelInput = z.object({ note: z.string().trim().max(1000).nullish() });
/**
 * "Clients covered" by a representative payment: the client of a past-earnings line (an existing client, or a free-text
 * name when the client is not in the system), and extra clients the payment covers. Only client links change — never
 * the amount, bank details, approvals or instruction. overrideReason: the client is already "rep paid" (Jason / owner).
 */
export const payoutLineClientInput = z
  .object({ clientId: id.nullish(), clientName: optText(140), overrideReason: optText(500) })
  .refine((v) => !(v.clientId && v.clientName), { message: 'Choose a client or type a name, not both', path: ['clientName'] });
export const payoutClientLinkInput = z.object({ clientId: id, overrideReason: optText(500) });
export const payoutReviewInput = z.object({
  decision: z.enum(['approve', 'reject', 'request_correction', 'return']),
  note: optText(2000),
  /** The owner's approval is final; at or above the threshold it needs his fresh authenticator code (step-up token). */
  stepUpToken: z.string().min(20).max(200).optional(),
  /** "Verify bank details & approve": the reviewer verifies the bank details awaiting verification, then approves. */
  verifyBankDetails: z.boolean().optional(),
});
export const payoutFinanceInput = z.object({ decision: z.enum(['approve', 'reject']), note: optText(2000), acknowledgeWarnings: z.boolean().default(false), instructionHash: z.string().length(64), /** The owner approving at Finance's check (final; authenticator code when required). */ stepUpToken: z.string().min(20).max(200).optional() });
export const payoutFinalInput = z.object({ stepUpToken: z.string().min(20).max(200), instructionHash: z.string().length(64), confirmAmountCents: z.number().int().positive(), note: optText(2000) });
export const payoutManualPaymentInput = z.object({ method: z.string().trim().min(2).max(60), reference: z.string().trim().min(2).max(120), paidAt: z.coerce.date(), bankAccount: optText(80), note: optText(1000) });
export const payoutSettingsInput = z.object({ perPaymentMaxCents: z.number().int().min(100).max(1_000_000_000), perDayMaxCents: z.number().int().min(100).max(10_000_000_000), slaHours: z.number().min(1).max(168), ownerApprovalThresholdCents: z.number().int().min(0).max(1_000_000_000).optional() });
const usd0 = (cents: number) => '$' + (cents / 100).toLocaleString('en-US', { maximumFractionDigits: 2 });
/** "Under $500 total: Jason's approval is final. $500 or more: owner's final approval." */
export const thresholdRule = (cents: number) => `Under ${usd0(cents)} total: Jason's approval is final. ${usd0(cents)} or more: owner's final approval.`;
export const acceptanceOverrideInput = z.object({ reason: z.string().trim().min(5).max(500) });

export const payoutRef = (n: number) => `RPO-${String(n).padStart(5, '0')}`;

// ---------------------------------------------------------------------------
// Payroll payouts (salaries, contractors, vendors): Finance prepares → owner approves with step-up → Airwallex
// ---------------------------------------------------------------------------
const period = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Use YYYY-MM');
/** Providers Finance can pay payroll through from the platform. */
export const PAYROLL_PROVIDERS = ['airwallex', 'mercury'] as const;
export type PayrollProvider = (typeof PAYROLL_PROVIDERS)[number];
/** Mercury's US methods (ACH / same-day ACH / domestic wire / RTP to a US routing + account number). */
export const MERCURY_DOMESTIC_METHODS = ['ach', 'same_day_ach', 'wire', 'rtp'] as const;
export const MERCURY_INTERNATIONAL_MESSAGE = 'Mercury pays bank accounts outside the US by international wire (SWIFT): these bank details have no SWIFT code — add Mercury international bank details, or use Airwallex';
export const MERCURY_USD_ONLY_MESSAGE = 'Mercury sends USD only (international wires go out in USD and the receiving bank converts) — enter the amount in USD, or use Airwallex';
/** What Mercury can pay, for messages. */
export const MERCURY_SCOPE = 'Mercury pays in USD: US accounts by ACH, real-time payment or domestic wire, and accounts in other countries by international wire';
/**
 * Whether Mercury can pay these bank details this amount, and why not: USD amounts only; US accounts by a domestic
 * method (USD), any other country by international (SWIFT) wire. Returns the reason it can't, or null.
 */
export function mercuryRefusal(b: { currency: string; bankCountry: string; method: string }, amountCurrency: string): string | null {
  if (amountCurrency.toUpperCase() !== 'USD') return MERCURY_USD_ONLY_MESSAGE;
  if (b.bankCountry.toUpperCase() === 'US') {
    if (b.currency.toUpperCase() !== 'USD' || !(MERCURY_DOMESTIC_METHODS as readonly string[]).includes(b.method)) return 'Mercury pays US bank accounts in USD by ACH, real-time payment or domestic wire — these US bank details are for another method';
    return null;
  }
  if (b.method !== 'swift') return MERCURY_INTERNATIONAL_MESSAGE;
  return null;
}
/** A Mercury international wire (any country other than the US). */
export const isMercuryInternational = (b: { provider?: string; bankCountry: string; method: string }) => (b.provider === undefined || b.provider === 'mercury') && b.bankCountry.toUpperCase() !== 'US' && b.method === 'swift';

/** How Mercury can send one payment (its `paymentMethod`: ach, realTimePayment, domesticWire, internationalWire). */
export const MERCURY_PAY_METHODS = ['ach', 'rtp', 'wire', 'swift'] as const;
export type MercuryPayMethod = (typeof MERCURY_PAY_METHODS)[number];
export const MERCURY_PAY_METHOD_LABELS: Record<MercuryPayMethod, string> = { ach: 'ACH (1–3 business days)', rtp: 'Real-time payment (RTP / instant)', wire: 'Domestic wire (same day)', swift: 'International wire (SWIFT, sent in USD)' };
/** Mercury methods for a bank country: US → ACH / RTP / domestic wire; any other country → international wire only. */
export const mercuryMethodsFor = (country: string): MercuryPayMethod[] => (country.toUpperCase() === 'US' ? ['ach', 'rtp', 'wire'] : ['swift']);
export const RTP_NOTE = "Real-time payments only work if the recipient's bank accepts RTP; if refused, switch to ACH.";
/** Ends the failure text of a refused / failed Mercury real-time payment. */
export const RTP_FAILURE_HINT = "→ Use 'Switch to ACH and resubmit'.";
/** A failure of a Mercury real-time payment that is about RTP itself (the recipient's bank doesn't take it). */
export const isRtpRefusal = (reason: string) => /real[- ]?time|\bRTP\b|realTimePayment/i.test(reason);
/** The failure text of a Mercury RTP payment, ending with the hint to switch to ACH (added once). */
export function withRtpHint(reason: string, provider: string, method: string) {
  if (provider !== 'mercury' || method !== 'rtp' || !isRtpRefusal(reason) || reason.trimEnd().endsWith(RTP_FAILURE_HINT)) return reason;
  return `${reason.trimEnd().replace(/[.\s]+$/, '')}. ${RTP_FAILURE_HINT}`;
}
/** Ends the failure text of an Airwallex payment refused for lack of wallet balance. */
export const AIRWALLEX_BALANCE_HINT = "→ Top up the Airwallex wallet and Retry, or use 'Send through Mercury instead'.";
/** Airwallex refused the transfer because the wallet can't fund it ("There is insufficient Wallet balance…"). */
export const isInsufficientBalance = (reason: string) => /insufficient\b.{0,40}\bbalance|insufficient[_ ]funds?/i.test(reason);
/** The failure text of a payment, ending with the hint that points to the fix (RTP refused → ACH; no wallet balance → top up or Mercury), added once. */
export function withFailureHint(reason: string, provider: string, method: string) {
  const r = withRtpHint(reason, provider, method);
  if (provider !== 'airwallex' || !isInsufficientBalance(r) || r.trimEnd().endsWith(AIRWALLEX_BALANCE_HINT)) return r;
  return `${r.trimEnd().replace(/[.\s…]+$/, '')}. ${AIRWALLEX_BALANCE_HINT}`;
}
/** "Switch to ACH and resubmit" / "Change payment method" on a failed Mercury payment. */
export const payoutMethodChangeInput = z.object({ method: z.enum(MERCURY_PAY_METHODS), note: z.string().trim().max(1000).nullish() });
/**
 * "Send through Mercury instead" / "Send through Airwallex instead" on a failed payment (or "Retry through Airwallex"
 * after a wallet top-up): a new instruction (new hash and instruction id) that needs the approvals again.
 */
export const payoutProviderChangeInput = z.object({
  provider: z.enum(['airwallex', 'mercury']),
  /** Mercury only: ACH, real-time payment or domestic wire (default ACH). */
  method: z.enum(MERCURY_PAY_METHODS).optional(),
  fundingAccountId: z.string().trim().min(1).max(120),
  note: z.string().trim().max(1000).nullish(),
});
/** Same-day ACH: Mercury's API has no separate same-day ACH method (it sends ACH), so it isn't offered for Mercury. */
/**
 * Methods a Mercury recipient can receive: one created with electronic routing info (ACH or RTP details) takes ACH
 * or RTP; a domestic-wire recipient takes wires only; an international recipient takes international wires only.
 */
export function mercuryPayMethods(b: { method: string }): MercuryPayMethod[] {
  if (b.method === 'wire') return ['wire'];
  if (b.method === 'swift') return ['swift'];
  if (['ach', 'same_day_ach', 'rtp'].includes(b.method)) return ['ach', 'rtp'];
  return [];
}
export const payrollSettingsInput = z.object({ defaultProvider: z.enum(PAYROLL_PROVIDERS) });
export const DEFAULT_PAYROLL_SETTINGS: { defaultProvider: PayrollProvider } = { defaultProvider: 'airwallex' };
const ccy = z.string().trim().regex(/^[A-Za-z]{3}$/).transform((v) => v.toUpperCase());
// ---------------------------------------------------------------------------
// Representative payments from the dashboard (Representative Payouts / the representative's page)
// ---------------------------------------------------------------------------
/**
 * Payout kinds: settlement = representative earnings lines (drop-offs, mail, a weekly settlement paid by transfer);
 * rep_payment = an ad-hoc representative payment (fee, reimbursement, bonus, advance); payroll = internal salaries;
 * opspay = an operational payment request (client-service expense) paid to a saved vendor payee by bank transfer.
 */
export const PAYOUT_KINDS = ['settlement', 'rep_payment', 'payroll', 'opspay'] as const;
export type PayoutKind = (typeof PAYOUT_KINDS)[number];
/** payees.kind of a saved payee (vendor) for operational payments: organisation-owned, never on Payroll. */
export const OPS_PAYEE_KIND = 'ops_vendor';
export const REP_PAYMENT_CATEGORIES = ['rep_fee', 'expense_reimbursement', 'bonus', 'advance', 'other'] as const;
export type RepPaymentCategory = (typeof REP_PAYMENT_CATEGORIES)[number];
export const REP_PAYMENT_CATEGORY_LABELS: Record<RepPaymentCategory, string> = {
  rep_fee: 'Representative fee (per client, per year)',
  expense_reimbursement: 'Expense reimbursement',
  bonus: 'Bonus',
  advance: 'Advance',
  other: 'Other',
};
/** Ledger category when the payment is paid: reimbursements are expenses; everything else is a representative payment. */
export const repPaymentFinanceCategory = (c: RepPaymentCategory) => (c === 'expense_reimbursement' ? 'other_expense' : 'rep_payments');
/**
 * Representative payments are sent through Airwallex by default. Mercury is offered too, but only while the Mercury
 * integration is enabled and verified (it needs the server's IPs whitelisted): USD to US bank accounts only, to
 * verified Mercury bank details of the representative.
 */
export const REP_PAYMENT_PROVIDER = 'airwallex' as const;
export const REP_PAYMENT_PROVIDERS = ['airwallex', 'mercury'] as const;
export type RepPaymentProvider = (typeof REP_PAYMENT_PROVIDERS)[number];
export const MERCURY_NOT_USABLE_MESSAGE = 'Mercury is not available: it must be enabled and verified in Admin → Integrations (the server IPs must be whitelisted on the Mercury API token). Pay through Airwallex.';
export const repPaymentCreateInput = z.object({
  /** Airwallex (default) or Mercury (only when the Mercury integration is enabled and verified). */
  provider: z.enum(REP_PAYMENT_PROVIDERS).default('airwallex'),
  /** Mercury only: ACH or real-time payment (defaults to the bank details' method). */
  method: z.enum(MERCURY_PAY_METHODS).optional(),
  representativeId: id,
  amountCents: z.number().int().positive().max(1_000_000_000),
  /** USD (wallet debit, the default) or the representative's own bank currency (they receive exactly this amount). */
  amountCurrency: ccy.default('USD'),
  category: z.enum(REP_PAYMENT_CATEGORIES),
  description: z.string().trim().min(3).max(500),
  fundingAccountId: z.string().trim().min(1).max(120),
  note: optText(2000),
  clientRequestId: z.string().uuid().optional(),
  /**
   * New Airwallex bank details entered with the payment when the representative has none (same body as the bank-details
   * form: provider + schema + airwallexFields, or the structured fields). Saved for verification by someone else.
   */
  bank: z.record(z.unknown()).nullish(),
});
export const settlementTransferInput = z.object({
  provider: z.enum(REP_PAYMENT_PROVIDERS).default('airwallex'),
  method: z.enum(MERCURY_PAY_METHODS).optional(),
  fundingAccountId: z.string().trim().min(1).max(120),
  note: optText(2000),
  clientRequestId: z.string().uuid().optional(),
});
/** What a representative sees of a transfer to them: never the internal approval steps. */
export type RepTransferState = 'pending' | 'sent' | 'paid' | 'failed';
export function repTransferState(status: PayoutStatus): RepTransferState {
  if (status === 'paid') return 'paid';
  if (['failed', 'reversed', 'cancelled', 'rejected'].includes(status)) return 'failed';
  if (['submitted', 'awaiting_bank_approval', 'processing', 'sent'].includes(status)) return 'sent';
  return 'pending';
}
export const REP_TRANSFER_STATE_LABELS: Record<RepTransferState, string> = { pending: 'Pending approval', sent: 'Sent — on its way to your bank', paid: 'Paid', failed: 'Not completed' };

// ---------------------------------------------------------------------------
// Past earnings: what representatives are still owed for past work (entered by Paul / Melina)
// ---------------------------------------------------------------------------
export const PAST_EARNING_KINDS = ['card_received', 'package_received', 'forwarded', 'mail', 'other'] as const;
export type PastEarningKind = (typeof PAST_EARNING_KINDS)[number];
export const PAST_EARNING_KIND_LABELS: Record<PastEarningKind, string> = { card_received: 'Card received', package_received: 'Package received', forwarded: 'Forwarded', mail: 'Mail', other: 'Other' };
export const pastEarningLineInput = z
  .object({
    serviceDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD'),
    clientId: id.nullish(),
    /** When the client is not in the system. */
    clientName: optText(140),
    serviceKind: z.enum(PAST_EARNING_KINDS),
    /** Tracking number or shipment reference (SHP-000123); linked to the shipment when it matches. */
    reference: optText(120),
    feeCents: z.number().int().min(0).max(10_000_000),
    reimbursementCents: z.number().int().min(0).max(10_000_000).default(0),
    note: optText(1000),
  })
  .refine((v) => v.feeCents + v.reimbursementCents > 0, { message: 'Enter the fee owed or a reimbursement', path: ['feeCents'] })
  .refine((v) => v.serviceDate <= new Date().toISOString().slice(0, 10), { message: 'The date of service cannot be in the future', path: ['serviceDate'] });
export type PastEarningLineInput = z.infer<typeof pastEarningLineInput>;
export const pastEarningsInput = z.object({
  representativeId: id,
  lines: z.array(pastEarningLineInput).min(1).max(100),
  /** Lines for clients already marked "rep paid": why the representative is paid again (Jason / the owner only). */
  overrideReason: optText(500),
});

// ---------------------------------------------------------------------------
// "Rep paid" marks on clients: the representative was already paid for this client (never pay twice)
// ---------------------------------------------------------------------------
/** manual: the toggle; bulk: the clients list multi-select; upload: a bulk upload; payout: a paid representative payout. */
export const REP_PAID_SOURCES = ['manual', 'bulk', 'upload', 'payout'] as const;
export type RepPaidSource = (typeof REP_PAID_SOURCES)[number];
export const REP_PAID_SOURCE_LABELS: Record<RepPaidSource, string> = { manual: 'Marked by hand', bulk: 'Marked in bulk', upload: 'Bulk upload', payout: 'Paid through a representative payout' };
const isoDay = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD');
export const clientRepPaidInput = z.object({
  paid: z.boolean(),
  /** Defaults to the representative of the client's current address assignment. */
  representativeId: id.nullish(),
  /** The payment date (defaults to today). The mark expires one year later. */
  paidAt: isoDay.optional(),
  note: optText(1000),
});
export const clientRepPaidBulkInput = z.object({ clientIds: z.array(id).min(1).max(500), paid: z.boolean(), paidAt: isoDay.optional(), note: optText(1000) });
/** paid: an unexpired mark; unpaid: none (never paid, or due again); due: due again or expiring within 30 days. */
export const REP_PAID_FILTERS = ['paid', 'unpaid', 'due'] as const;
export type RepPaidFilter = (typeof REP_PAID_FILTERS)[number];
/** Days before expiry during which a mark is shown as "expiring soon". */
export const REP_PAID_EXPIRING_DAYS = 30;

/** A "rep paid" mark expires one year after the payment date (same calendar day; 29 Feb → 28 Feb). */
export function repPaidExpiresOn(paidAt: string): string {
  const [y, m, d] = paidAt.slice(0, 10).split('-').map(Number);
  const leap = (n: number) => n % 4 === 0 && (n % 100 !== 0 || n % 400 === 0);
  const day = m === 2 && d === 29 && !leap(y + 1) ? 28 : d;
  return `${y + 1}-${String(m).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/**
 * A client's "rep paid" status: paid (an unexpired mark), due_again (the last payment expired) or unpaid (never
 * marked), from the latest relevant mark. Shown on the clients list and the client page.
 */
export interface ClientRepPaid {
  state: 'paid' | 'due_again' | 'unpaid';
  /** An unexpired paid mark exists. */
  paid: boolean;
  paidAt: string | null;
  /** paidAt + 1 year: from that day the client is due again. */
  expiresOn: string | null;
  /** Paid, expiring within 30 days. */
  expiringSoon: boolean;
  markedByName: string | null;
  representativeId: string | null;
  representativeName: string | null;
  source: RepPaidSource | null;
  /** Unexpired paid marks for this client (one per representative). */
  count: number;
}

/** A client already marked "rep paid" for a representative (warning before entering / paying it again). */
export interface RepPaidMark {
  clientId: string;
  clientName: string;
  representativeId: string | null;
  representativeName: string | null;
  paidAt: string;
  expiresOn: string;
  markedByName: string | null;
  source: RepPaidSource;
}

/**
 * Shipment statuses meaning the card / package left the representative (shipped on to the hub or the client, or
 * delivered / closed). Used to suggest clients in bulk when entering past earnings.
 */
export const PAST_EARNING_SHIPPED_STATUSES = ['in_transit_to_hub', 'received_at_hub', 'forwarded_to_client', 'delivered_to_client', 'closed'] as const;

/** First amount in a representative's agreed pricing text ("$25 per client / year" → "25"), for prefilling a fee. */
export function feeFromAgreedPricing(text: string | null | undefined): string | null {
  const m = /(\d+(?:[.,]\d{1,2})?)/.exec(text ?? '');
  return m ? m[1].replace(',', '.') : null;
}

export const pastEarningCandidatesQuery = z.object({
  representativeId: id,
  from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD').optional(),
  to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD').optional(),
  includeAssigned: z.preprocess((v) => v === true || v === 'true' || v === '1', z.boolean()).default(false),
});

/** A client of the representative to add as a past-earnings line (one per client per calendar year). */
export interface PastEarningCandidate {
  clientId: string;
  clientName: string;
  year: number;
  /** shipment: shipped / delivered shipments; assigned: assigned to the rep's address, no shipment recorded in the range. */
  source: 'shipment' | 'assigned';
  /** Suggested date of service: the latest ship date (or, for assigned clients, the assignment date within the range). */
  serviceDate: string;
  latestShipDate: string | null;
  shipmentCount: number;
  trackingNumbers: string[];
  latestTracking: string | null;
  latestShipmentId: string | null;
  latestShipmentRef: string | null;
  serviceKind: 'card_received' | 'package_received';
  /** A fee line (past earnings or a settlement / payout line) already exists for this rep + client in that year. */
  existingFee: { count: number; feeCents: number; serviceDate: string; description: string } | null;
  /** The client is marked "rep paid" for this representative (or for an unknown one): not selected by default. */
  repPaid: RepPaidMark | null;
}

export const payrollPayoutInput = z.object({
  payeeId: id,
  period,
  amountCents: z.number().int().positive().max(1_000_000_000),
  /** USD (wallet debit) or the person's own currency (they receive exactly this amount). */
  amountCurrency: ccy,
  /** Defaults to the payroll setting (Airwallex). */
  provider: z.enum(PAYROLL_PROVIDERS).optional(),
  /** Mercury only: ACH, real-time payment or wire for this payment (defaults to the bank details' method). */
  method: z.enum(MERCURY_PAY_METHODS).optional(),
  fundingAccountId: z.string().trim().min(1).max(120),
  purpose: z.string().trim().min(3).max(300).optional(),
  note: optText(2000),
  /** A second payment to the same person for the same month (bonus, correction). */
  allowAdditional: z.boolean().default(false),
  clientRequestId: z.string().uuid().optional(),
});
export const payrollPayoutUpdateInput = z.object({
  amountCents: z.number().int().positive().max(1_000_000_000).optional(),
  amountCurrency: ccy.optional(),
  fundingAccountId: z.string().trim().min(1).max(120).optional(),
  method: z.enum(MERCURY_PAY_METHODS).optional(),
  purpose: z.string().trim().min(3).max(300).optional(),
  note: optText(2000),
});
export const payrollBatchInput = z.object({
  period,
  /** The run's default provider (rows may override it). Defaults to the payroll setting. */
  provider: z.enum(PAYROLL_PROVIDERS).optional(),
  /** Funding account of the run's default provider. */
  fundingAccountId: z.string().trim().min(1).max(120),
  /** Funding accounts per provider, for rows that override the provider. */
  fundingAccounts: z.object({ airwallex: z.string().trim().min(1).max(120).optional(), mercury: z.string().trim().min(1).max(120).optional() }).optional(),
  note: optText(2000),
  items: z.array(z.object({ payeeId: id, amountCents: z.number().int().positive().max(1_000_000_000), amountCurrency: ccy, provider: z.enum(PAYROLL_PROVIDERS).optional(), method: z.enum(MERCURY_PAY_METHODS).optional() })).min(1).max(200),
  clientRequestId: z.string().uuid().optional(),
});
export const payrollBatchFinalInput = z.object({
  stepUpToken: z.string().min(20).max(200),
  /** The exact instructions the owner saw; any change since then refuses the approval. */
  items: z.array(z.object({ id, instructionHash: z.string().length(64) })).min(1).max(200),
  note: optText(2000),
});
/** submitted = waiting for Jason's review · reviewed = waiting for the owner's final approval · returned = sent back to Finance. */
export const PAYROLL_BATCH_STATUSES = ['submitted', 'reviewed', 'returned', 'rejected', 'approved', 'completed', 'cancelled'] as const;
export type PayrollBatchStatus = (typeof PAYROLL_BATCH_STATUSES)[number];
export const PAYROLL_BATCH_STATUS_LABELS: Record<PayrollBatchStatus, string> = {
  submitted: 'Waiting for Jason’s review',
  reviewed: 'Reviewed — waiting for final approval',
  returned: 'Returned to Finance',
  rejected: 'Rejected',
  approved: 'Approved — paying',
  completed: 'Payroll complete',
  cancelled: 'Cancelled',
};
/** "2026-10" → "October 2026". */
export function periodLabel(period: string) {
  const [y, m] = period.split('-').map(Number);
  if (!y || !m) return period;
  return `${['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'][m - 1]} ${y}`;
}
export const payrollBatchRef = (n: number) => `PRB-${String(n).padStart(4, '0')}`;

// ---------------------------------------------------------------------------
// Beneficiary field schema (what Airwallex asks for, per country / currency / method)
// ---------------------------------------------------------------------------
export const BENEFICIARY_TRANSFER_METHODS = ['LOCAL', 'SWIFT'] as const;
export type BeneficiaryTransferMethod = (typeof BENEFICIARY_TRANSFER_METHODS)[number];

export interface BeneficiarySchemaCondition {
  country: string; // bank country, 2 letters
  currency: string; // account currency, 3 letters
  entityType: 'PERSONAL' | 'COMPANY';
  transferMethod: BeneficiaryTransferMethod;
  /** Local clearing system, e.g. ACH or FEDWIRE for US accounts. */
  clearing?: string | null;
}

export interface BeneficiaryField {
  key: string;
  /** Airwallex API path, e.g. beneficiary.bank_details.account_number. */
  path: string;
  label: string;
  type: 'text' | 'select' | 'date' | 'email' | 'tel';
  required: boolean;
  /** Regular expression the value must match (from Airwallex's rule.pattern). */
  pattern?: string | null;
  options?: { label: string; value: string }[];
  placeholder?: string | null;
  help?: string | null;
  example?: string | null;
  default?: string | null;
  /** Fixed by the provider (e.g. Mercury recipients are always in the US): shown but not editable. */
  readOnly?: boolean;
  group: 'recipient' | 'bank' | 'address' | 'additional';
}

export interface BeneficiarySchema {
  /** airwallex = Airwallex's live form schema; standard = built-in standard fields (Airwallex not connected / unavailable). */
  source: 'airwallex' | 'standard' | 'mercury';
  condition: BeneficiarySchemaCondition;
  fields: BeneficiaryField[];
}

export const beneficiarySchemaQuery = z.object({
  country: z.string().trim().regex(/^[A-Za-z]{2}$/).transform((v) => v.toUpperCase()),
  currency: z.string().trim().regex(/^[A-Za-z]{3}$/).transform((v) => v.toUpperCase()),
  entityType: z.enum(['PERSONAL', 'COMPANY']).default('PERSONAL'),
  method: z.enum(BENEFICIARY_TRANSFER_METHODS).default('LOCAL'),
  clearing: z.string().trim().max(30).optional(),
  /** Which provider's recipient form to show (Mercury has its own field set). */
  provider: z.enum(['airwallex', 'mercury', 'manual']).default('airwallex'),
});

/** Payout method stored for a beneficiary chosen through the schema form. */
export function payoutMethodFor(c: Pick<BeneficiarySchemaCondition, 'country' | 'transferMethod' | 'clearing'>): PayoutMethod {
  if (c.transferMethod === 'SWIFT') return 'swift';
  if (c.country === 'US') {
    const k = (c.clearing ?? 'ACH').toUpperCase();
    return k === 'FEDWIRE' ? 'wire' : k === 'RTP' ? 'rtp' : 'ach';
  }
  return 'local';
}

/** Checks values against a schema: required fields and their patterns. Returns path → message. */
/**
 * Address fields follow the ADDRESS country, not the bank country: Airwallex describes the address for the bank's
 * country (e.g. Nepal's province list and Nepali examples), which is wrong for someone banking in Nepal but living in
 * the US. When the two differ, the state becomes a free text box (2 letters for the US) and country-specific examples
 * and patterns on the address are dropped.
 */
export function adaptAddressFields(fields: BeneficiaryField[], bankCountry: string, values: Record<string, string>): BeneficiaryField[] {
  const addr = (values['beneficiary.address.country_code'] ?? '').trim().toUpperCase();
  if (!addr || addr === bankCountry.toUpperCase()) return fields;
  return fields.map((f) => {
    if (!f.path.startsWith('beneficiary.address.') || f.path === 'beneficiary.address.country_code') return f;
    if (f.path === 'beneficiary.address.state')
      return { ...f, label: addr === 'US' ? 'State (2 letters, e.g. CA)' : 'State / province', type: 'text', options: undefined, help: null, example: null, placeholder: addr === 'US' ? 'CA' : null, pattern: addr === 'US' ? '[A-Za-z]{2}' : null };
    return { ...f, help: null, example: null, pattern: addr === 'US' && f.path === 'beneficiary.address.postcode' ? '\\d{5}(-\\d{4})?' : null };
  });
}

export function validateBeneficiaryFields(fields: BeneficiaryField[], values: Record<string, string>): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const f of fields) {
    const v = (values[f.path] ?? '').trim();
    if (!v) {
      if (f.required) errors[f.path] = `${f.label} is required`;
      continue;
    }
    if (f.readOnly && f.default && v.toUpperCase() !== f.default.toUpperCase()) errors[f.path] = `${f.label} must be ${f.default}`;
    else if (f.options?.length && !f.options.some((o) => o.value === v)) errors[f.path] = `Choose a valid ${f.label.toLowerCase()}`;
    else if (f.pattern) {
      let re: RegExp | null = null;
      try {
        re = new RegExp(`^(?:${f.pattern})$`);
      } catch {
        re = null; // a pattern we cannot evaluate is left to the provider's own validation
      }
      if (re && !re.test(v)) errors[f.path] = `${f.label} doesn't look right${f.example ? ` (e.g. ${f.example})` : ''}`;
    }
  }
  return errors;
}

/**
 * Derives the structured bank details kept for display, masking, Mercury and payout checks from the Airwallex field
 * values. Values already given explicitly are not overridden.
 */
export function structuredFromBeneficiaryFields(c: BeneficiarySchemaCondition, v: Record<string, string>) {
  const g = (...paths: string[]) => paths.map((p) => (v[p] ?? '').trim()).find(Boolean) ?? '';
  const first = g('beneficiary.first_name');
  const last = g('beneficiary.last_name');
  const company = g('beneficiary.company_name');
  const holderName = g('beneficiary.bank_details.account_name') || (c.entityType === 'COMPANY' ? company : [first, last].filter(Boolean).join(' '));
  const category = g('beneficiary.bank_details.bank_account_category').toLowerCase();
  const routing = g('beneficiary.bank_details.account_routing_value1');
  return {
    holderName,
    holderType: (c.entityType === 'COMPANY' ? 'company' : 'personal') as 'company' | 'personal',
    currency: c.currency,
    bankCountry: c.country,
    method: payoutMethodFor(c),
    clearingSystem: c.clearing ?? null,
    bankName: g('beneficiary.bank_details.bank_name') || null,
    accountNumber: g('beneficiary.bank_details.account_number', 'beneficiary.bank_details.iban'),
    routingNumber: routing || null,
    swiftCode: g('beneficiary.bank_details.swift_code') || null,
    accountType: (category.startsWith('sav') ? 'savings' : 'checking') as 'savings' | 'checking',
    taxId: g('beneficiary.additional_info.personal_id_number') || null,
    email: g('beneficiary.additional_info.personal_email') || null,
    address: {
      line1: g('beneficiary.address.street_address'),
      city: g('beneficiary.address.city'),
      state: g('beneficiary.address.state') || null,
      postcode: g('beneficiary.address.postcode'),
      country: (g('beneficiary.address.country_code') || c.country).toUpperCase(),
    },
  };
}
